import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, ExternalLink } from 'lucide-react';
import { IMAGES, TRAINERS, PRESS, STUDIO, VIDEOS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, CloseCTA, ProofStrip, PitVideo } from '../components/Shared';

/* ================= ABOUT ================= */
export function About() {
  usePageMeta(
    'About — The PITFIT Story',
    'PITFIT was founded in 2020 by Chris Wiese inside a Travis Walk parking garage in Uptown Dallas. This is the story of the PIT.'
  );

  const founder = TRAINERS[0];

  return (
    <>
      <PageHero
        tag="Our Story"
        title="From a concrete deck to a Dallas cult following."
        sub="PITFIT wasn’t built in a boardroom. It was built on a concrete deck at Travis Walk by a coach who believed Dallas deserved harder, more honest HIIT."
        video={VIDEOS.crew}
      />
      <ProofStrip />

      {/* FOUNDER */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={founder.img} alt="Chris Wiese, founder and head coach of PITFIT Dallas" className="rounded-2xl w-full h-[480px] object-cover object-top border border-[rgba(148,170,220,0.16)]" loading="lazy" data-testid="about-founder-image" />
          </Reveal>
          <Reveal delay={120}>
            <div className="flex items-center gap-2.5 mb-4">
              <Award size={16} style={{ color: 'var(--pf-brand2)' }} />
              <span className="mono-label !mb-0">13+ Years Coaching Dallas</span>
            </div>
            <h2 className="h-section text-white">Chris Wiese, Founder & Head Coach</h2>
            <p className="text-muted-pf mt-5 text-[17px] leading-relaxed">{founder.bio}</p>
            <p className="text-muted-pf mt-4 text-[17px] leading-relaxed">
              The garage wasn't a gimmick — it was the point. No mirrors to pose in, no machines to hide behind. Just a coach, a clock, and a room full of people pushing each other through the hardest hour of their day.
            </p>
            <div className="flex flex-wrap gap-2.5 mt-7">
              {founder.certs.map((c) => (
                <span key={c} className="font-mono-pf text-[11.5px] uppercase tracking-[0.1em] px-3.5 py-2 rounded-full border border-[rgba(148,170,220,0.25)] text-muted-pf">{c}</span>
              ))}
            </div>
            <Link to="/about/trainers" className="btn-ghost mt-8">Meet All Coaches <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* TIMELINE / FACTS */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="The Facts" title="PITFIT, in numbers." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ['2020', 'Founded at Travis Walk'],
              ['45–60', 'Minute classes, non-stop'],
              ['16–40', 'Class sizes, always coached'],
              [`${STUDIO.rating}★`, `${STUDIO.reviewCount}+ verified reviews`],
            ].map(([n, l], i) => (
              <Reveal key={l} delay={i * 70}>
                <div className="glass-card p-7 text-center h-full">
                  <p className="font-display font-bold text-4xl text-white">{n}</p>
                  <p className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-muted-pf mt-2">{l}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <Reveal>
              <div className="glass-card p-7 h-full">
                <p className="mono-label mb-3">The Mission</p>
                <p className="text-[#cdd5e4] text-[16px] leading-relaxed">Strip fitness back to what works: programmed intervals, real coaching, honest effort, and a room small enough that nobody disappears. Sixty minutes at a time.</p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="glass-card p-7 h-full">
                <p className="mono-label mb-3">The Name</p>
                <p className="text-[#cdd5e4] text-[16px] leading-relaxed">The PIT is the concrete deck we train on — a converted parking garage at Travis Walk. The name came first, before a single class was booked. The cult following came fast after.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRESS TEASER */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <SectionHeader tag="As Seen In" title="The press caught on." />
            <Link to="/about/press" className="btn-quiet mb-12">All press mentions <ArrowRight size={15} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {PRESS.map((p, i) => (
              <Reveal key={p.outlet} delay={i * 60}>
                <div className="glass-card glass-card-hover p-6 text-center">
                  <p className="font-display font-bold text-white text-lg">{p.outlet}</p>
                  <p className="mono-label mt-2">{p.tag}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CloseCTA title="The story continues at 6 AM." sub="Come write your chapter. $15 first class — no membership, no commitment." />
    </>
  );
}

/* ================= TRAINERS ================= */
export function Trainers() {
  usePageMeta(
    'Trainers — Meet the Coaches',
    'Meet PITFIT\u2019s coaching team — led by founder Chris Wiese. Certified coaches who know your name and watch your form.'
  );

  return (
    <>
      <PageHero
        tag="The Coaches"
        title="Coaches who know your name."
        sub="Classes at PITFIT are intentionally sized so coaching stays personal. Every coach is certified, every class is run — never just supervised."
      />

      <section className="section-pad !pt-8 !pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <img src="/images/team.jpg" alt="The PITFIT coaching team inside the Travis Walk parking garage studio in Dallas" className="rounded-2xl w-full h-[340px] md:h-[480px] object-cover object-top border border-[rgba(148,170,220,0.16)]" data-testid="trainers-team-photo" />
          </Reveal>
        </div>
      </section>

      <section className="section-pad !pt-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-6">
          {TRAINERS.map((t, i) => (
            <Reveal key={t.slug} delay={i * 60}>
              <div className="glass-card glass-card-hover overflow-hidden grid md:grid-cols-3">
                <img src={t.img} alt={`${t.name}, ${t.role} at PITFIT Dallas`} className="w-full h-64 md:h-full object-cover object-top" loading="lazy" data-testid={`trainer-image-${t.slug}`} />
                <div className="md:col-span-2 p-7 md:p-9">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-display font-bold text-white text-2xl">{t.name}</h2>
                  </div>
                  <p className="mono-label mt-2">{t.role}</p>
                  <p className="text-muted-pf text-[15.5px] mt-4 leading-relaxed">{t.bio}</p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {[...(t.certs || []), ...(t.classes || [])].map((c) => (
                      <span key={c} className="font-mono-pf text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 rounded-full border border-[rgba(148,170,220,0.22)] text-muted-pf">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad !pt-4 !pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <img src="/images/pit-studio.jpg" alt="Inside the PITFIT studio — steps, air bikes and the strength floor in the Travis Walk parking garage" className="rounded-2xl w-full h-[320px] md:h-[440px] object-cover border border-[rgba(148,170,220,0.16)]" loading="lazy" data-testid="trainers-studio-photo" />
            <p className="font-mono-pf text-[11.5px] uppercase tracking-[0.14em] text-muted-pf mt-3">The room they coach in — 3rd parking level, Travis Walk</p>
          </Reveal>
        </div>
      </section>

      <CloseCTA title="Train with them once. $15." sub="Pick any class — whichever coach is on the podium, you're in good hands." />
    </>
  );
}

/* ================= PRESS ================= */
export function Press() {
  usePageMeta(
    'Press — PITFIT in the Media',
    'PITFIT press coverage: featured in D Magazine, named among Knox-Henderson\u2019s best gyms by PaperCity, profiled by the Dallas Observer and Modern Luxury.'
  );

  return (
    <>
      <PageHero
        tag="As Seen In"
        title="What Dallas media says about the PIT."
        sub="We didn’t pay for any of this. The parking-garage HIIT concept earned its own headlines."
      />

      <section className="section-pad !pt-8">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-5">
          {PRESS.map((p, i) => (
            <Reveal key={p.outlet} delay={i * 60}>
              <div className="glass-card glass-card-hover p-7 md:p-9 flex flex-col md:flex-row gap-6 md:items-center justify-between">
                <div>
                  <span className="mono-label">{p.tag}</span>
                  <h2 className="font-display font-bold text-white text-2xl mt-2">{p.outlet}</h2>
                  <p className="text-[#cdd5e4] text-[16px] mt-3 leading-relaxed max-w-2xl">“{p.quote}”</p>
                </div>
                <span className="btn-ghost shrink-0 !min-h-[44px] !py-2 opacity-80 cursor-default">
                  Read Feature <ExternalLink size={15} />
                </span>
              </div>
            </Reveal>
          ))}

          <Reveal delay={120}>
            <div className="glass-card p-7 text-center">
              <p className="mono-label mb-2">Media Inquiries</p>
              <p className="text-muted-pf text-[15px]">Writing about Dallas fitness? We'd love to show you the PIT. Reach us at <a href={`mailto:${STUDIO.email}`} className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>{STUDIO.email}</a></p>
            </div>
          </Reveal>
        </div>
      </section>

      <CloseCTA title="See what the fuss is about." sub="The press got a tour. You get the full 60 minutes — for $15." />
    </>
  );
}
